import { useEffect, useMemo, useState } from 'react';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { inputVariants } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface SshConfigAlias {
  alias: string;
  hostName?: string;
  user?: string;
  port?: number;
  identityFile?: string;
  proxyJump?: string;
}

interface Props {
  /** The input's id (the SSH host label's `htmlFor`). */
  id?: string;
  /** The field has a validation error (red border). */
  invalid?: boolean;
  value: string;
  onChange: (value: string) => void;
  /** Fires when the user picks a non-ProxyJump alias from the dropdown.
   *  The parent uses this to pre-fill sibling SSH fields (user, port, key path). */
  onSelectAlias: (alias: SshConfigAlias) => void;
}

const MAX_MATCHES = 8;

export function SshHostCombobox({ id, invalid, value, onChange, onSelectAlias }: Props) {
  const [aliases, setAliases] = useState<SshConfigAlias[]>([]);
  // Internal query drives filtering; initialized from value prop.
  // The parent controls the persisted value, but we need a local state so the
  // dropdown can filter while the parent processes onChange asynchronously.
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const { track } = useAnalytics();

  // Sync external value changes (e.g. form reset) back into the query state.
  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    window.electronAPI?.ssh
      ?.readConfigAliases()
      .then((list: SshConfigAlias[]) => setAliases(list ?? []))
      .catch(() => setAliases([]));
  }, []);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return aliases.filter((a) => a.alias.toLowerCase().startsWith(q)).slice(0, MAX_MATCHES);
  }, [query, aliases]);

  const handleSelect = (alias: SshConfigAlias) => {
    // M11 — ProxyJump entries are rendered aria-disabled with no click
    // handler; if for any reason this still gets invoked for a proxyJump
    // alias (e.g. keyboard activation in the future), bail silently. We
    // still track the analytics event so we can see how often users hit
    // the unsupported path. Properties only carry the low-cardinality
    // `has_proxy_jump` flag — never the alias name or hostname (PII).
    track('feature:ssh_config_alias_picked', {
      has_proxy_jump: !!alias.proxyJump,
    });
    if (alias.proxyJump) {
      setOpen(false);
      return;
    }
    onSelectAlias(alias);
    setOpen(false);
  };

  return (
    <div className="relative">
      <input
        id={id}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open && matches.length > 0}
        type="text"
        value={query}
        onChange={(e) => {
          const next = e.target.value;
          setQuery(next);
          onChange(next);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        data-testid="ssh-host-input"
        className={cn(inputVariants(), invalid && 'border-destructive focus-visible:ring-destructive')}
        placeholder="bastion or bastion.corp.com"
      />
      {open && matches.length > 0 && (
        <ul
          role="listbox"
          className="absolute z-10 mt-1 w-full rounded-md border bg-popover shadow-md max-h-48 overflow-y-auto"
        >
          {matches.map((a) => {
            // M11 — ProxyJump aliases stay visible (so users see the alias
            // exists) but are rendered aria-disabled with cursor-not-allowed
            // and no click handler. We don't filter them out — invisibility
            // would just confuse users who know the alias name and would
            // expect it in the list.
            const proxy = !!a.proxyJump;
            return (
              <li
                key={a.alias}
                role="option"
                aria-selected={false}
                aria-disabled={proxy || undefined}
                data-testid={`ssh-alias-option-${a.alias}`}
                className={
                  proxy
                    ? 'cursor-not-allowed px-3 py-1.5 text-sm opacity-60'
                    : 'cursor-pointer px-3 py-1.5 text-sm hover:bg-accent'
                }
                title={proxy ? 'Multi-hop tunnels (ProxyJump) are not supported.' : undefined}
                onClick={proxy ? undefined : () => handleSelect(a)}
              >
                <span className="font-medium">{a.alias}</span>
                {a.hostName && (
                  <span className="ml-2 text-xs text-muted-foreground">{a.hostName}</span>
                )}
                {a.proxyJump && (
                  <span className="ml-2 text-xs text-warning">⛔ ProxyJump</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
